import React from 'react'
import { Link, useForm, usePage } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { format } from 'date-fns'
import { ja } from 'date-fns/locale'
import { CreditCard, User, KeyRound, AlertTriangle, Key } from 'lucide-react'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'

type Props = {
  user: {
    email: string
    username: string
    createdAt: string
  }
  passwordErrors?: Record<string, string[]>
}

export default function SettingsIndex({ user, passwordErrors = {} }: Props) {
  const { flash } = usePage().props
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)

  const { data, setData, patch, processing, reset } = useForm({
    current_password: '',
    password: '',
    password_confirmation: '',
  })

  const deleteForm = useForm({
    password: '',
  })

  // flash notice/alert があればトースト表示
  React.useEffect(() => {
    if (flash?.notice) {
      toast.success(flash.notice)
    }
    if (flash?.alert) {
      toast.error(flash.alert)
    }
  }, [flash?.notice, flash?.alert])

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch('/settings/password', {
      onSuccess: () => {
        reset()
      },
    })
  }

  const handleDeleteAccount = () => {
    deleteForm.delete('/settings', {
      onError: () => {
        setDeleteDialogOpen(false)
        deleteForm.reset()
      },
    })
  }

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold">アカウント設定</h1>
          <p className="text-muted-foreground">アカウント情報の確認と変更</p>
        </div>

        {/* アカウント情報 */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              アカウント情報
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-muted-foreground">ユーザー名</Label>
              <p className="font-medium">{user.username}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">メールアドレス</Label>
              <p className="font-medium">{user.email}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">登録日</Label>
              <p className="font-medium">
                {format(new Date(user.createdAt), 'yyyy年M月d日', { locale: ja })}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* パスワード変更 */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5" />
              パスワード変更
            </CardTitle>
            <CardDescription>
              パスワードを変更できます
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current_password">現在のパスワード</Label>
                <Input
                  id="current_password"
                  type="password"
                  value={data.current_password}
                  onChange={(e) => setData('current_password', e.target.value)}
                  required
                />
                {passwordErrors.current_password && (
                  <p className="text-sm text-destructive">{passwordErrors.current_password[0]}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">新しいパスワード</Label>
                <Input
                  id="password"
                  type="password"
                  value={data.password}
                  onChange={(e) => setData('password', e.target.value)}
                  minLength={12}
                  required
                />
                {passwordErrors.password && (
                  <p className="text-sm text-destructive">{passwordErrors.password[0]}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  12文字以上、特殊文字を1文字以上含む
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password_confirmation">新しいパスワード（確認）</Label>
                <Input
                  id="password_confirmation"
                  type="password"
                  value={data.password_confirmation}
                  onChange={(e) => setData('password_confirmation', e.target.value)}
                  minLength={12}
                  required
                />
                {passwordErrors.password_confirmation && (
                  <p className="text-sm text-destructive">{passwordErrors.password_confirmation[0]}</p>
                )}
              </div>

              <Button type="submit" disabled={processing}>
                {processing ? 'パスワードを変更中...' : 'パスワードを変更'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* プラン・課金 */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              プラン・課金
            </CardTitle>
            <CardDescription>
              プランの確認と変更ができます
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link href="/settings/billing">
              <Button variant="outline">プラン・課金管理へ</Button>
            </Link>
          </CardContent>
        </Card>

        {/* Docker CLI */}
        <Card>
          <CardHeader>
            <CardTitle>Docker CLI</CardTitle>
            <CardDescription>
              コンテナレジストリへの接続方法
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-muted-foreground">ログインコマンド</Label>
              <div className="bg-muted rounded-lg p-3 mt-1">
                <code className="text-sm font-mono">
                  docker login {typeof window !== 'undefined' && window.location.hostname === 'localhost' ? 'localhost:5001' : 'registry.konreji.com'}
                </code>
              </div>
            </div>
            <div>
              <Label className="text-muted-foreground">認証情報</Label>
              <p className="text-sm text-muted-foreground mt-1">
                ユーザー名: <span className="font-mono">{user.email}</span> または <span className="font-mono">{user.username}</span>
                <br />
                パスワード: ログインパスワード または APIトークン
              </p>
            </div>
          </CardContent>
        </Card>

        {/* APIトークン */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              APIトークン
            </CardTitle>
            <CardDescription>
              CI/CD用のアクセストークンを管理します
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link href="/settings/api_tokens">
              <Button variant="outline">APIトークン管理へ</Button>
            </Link>
          </CardContent>
        </Card>

        {/* アカウント削除 */}
        <Card className="border-destructive/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              アカウント削除
            </CardTitle>
            <CardDescription>
              アカウントを削除すると、すべてのリポジトリとイメージが完全に削除されます。この操作は取り消せません。
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
              <AlertDialogTrigger asChild>
                <Button variant="destructive">アカウントを削除</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>本当にアカウントを削除しますか？</AlertDialogTitle>
                  <AlertDialogDescription className="space-y-2">
                    <p>この操作により以下が完全に削除されます：</p>
                    <ul className="list-disc list-inside text-sm">
                      <li>すべてのリポジトリ</li>
                      <li>すべてのコンテナイメージ</li>
                      <li>サブスクリプション（Pro プランの場合）</li>
                    </ul>
                    <p className="font-medium text-destructive">この操作は取り消せません。</p>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="py-4">
                  <Label htmlFor="delete_password">パスワードを入力して確認</Label>
                  <Input
                    id="delete_password"
                    type="password"
                    value={deleteForm.data.password}
                    onChange={(e) => deleteForm.setData('password', e.target.value)}
                    placeholder="パスワード"
                    className="mt-2"
                  />
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel onClick={() => deleteForm.reset()}>キャンセル</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeleteAccount}
                    disabled={!deleteForm.data.password || deleteForm.processing}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {deleteForm.processing ? '削除中...' : 'アカウントを削除'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
